import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import { BASE_URL, ROLE } from '../../components/constants';
import { authHeaders } from '../../utils/authHeaders';
import MultiSelectDropdown from '../../components/sub-components/MultiSelectDropdown';

// Defaults applied to an account the first time it signs in with Microsoft.
// SUPER_ADMIN only — the server enforces that as well; this modal only hides it.
//
// Groups are departments, and a department belongs to exactly one organization,
// so the group list is refetched whenever the organization changes and any
// selection from the previous org is dropped. The API rejects a cross-org group,
// so keeping the UI in step avoids an error the admin cannot act on.
const NewUserDefaultsModal = ({ isOpen, onClose, aispaceColor }) => {
  const { t } = useTranslation();

  const [organizationId, setOrganizationId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [departmentIds, setDepartmentIds] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadDepartments = useCallback(async (orgId) => {
    if (!orgId) {
      setDepartments([]);
      return [];
    }
    try {
      const res = await axios.get(`${BASE_URL}/departments/${orgId}`, authHeaders());
      const list = res.data.departments || [];
      setDepartments(list);
      return list;
    } catch {
      setDepartments([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const [defaultsRes, orgsRes] = await Promise.all([
          axios.get(`${BASE_URL}/settings/new-user-defaults`, authHeaders()),
          axios.get(`${BASE_URL}/organizations`, authHeaders()),
        ]);
        if (cancelled) return;

        const saved = defaultsRes.data || {};
        setOrganizations(orgsRes.data.organizations || []);
        setOrganizationId(saved.organization_id || '');
        setUserRole(saved.user_role || ROLE.NORMAL);

        // Keep only saved groups that still exist inside the saved organization.
        const list = await loadDepartments(saved.organization_id);
        if (cancelled) return;
        const valid = new Set(list.map((d) => String(d.id)));
        setDepartmentIds((saved.department_ids || []).map(String).filter((id) => valid.has(id)));
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || t('common.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, loadDepartments, t]);

  const handleOrgChange = async (orgId) => {
    setOrganizationId(orgId);
    setDepartmentIds([]); // groups from the previous org are not valid here
    await loadDepartments(orgId);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(
        `${BASE_URL}/settings/new-user-defaults`,
        {
          organization_id: organizationId || null,
          user_role: userRole || null,
          department_ids: departmentIds,
        },
        authHeaders(),
      );
      onClose(true);
    } catch (err) {
      setError(err?.response?.data?.error || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const selectClass =
    'w-full px-3 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg bg-white focus:outline-none';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-xl w-full max-w-[520px] max-h-[90vh] min-h-[440px] overflow-y-auto flex flex-col">
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-[20px] font-bold text-[#111827]">{t('users.newUserDefaultsTitle')}</h2>
          <p className="text-[13px] text-[#6B7280] mt-0.5">{t('users.newUserDefaultsSubtitle')}</p>
        </div>

        {error && (
          <div className="mx-6 mb-4 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="animate-spin" size={22} />
          </div>
        ) : (
          <div className="px-6 pb-2 space-y-5 flex-1">
            <div>
              <label className="block text-[14px] font-medium text-[#111827] mb-1.5">
                {t('users.addUserOrganizationLabel')}
              </label>
              <select
                className={selectClass}
                value={organizationId}
                onChange={(e) => handleOrgChange(e.target.value)}
              >
                <option value="">—</option>
                {organizations.map((o) => (
                  <option key={o.organization_id} value={o.organization_id}>
                    {o.organization_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[14px] font-medium text-[#111827] mb-1.5">
                {t('users.addUserRoleLabel')}
              </label>
              <select
                className={selectClass}
                value={userRole}
                onChange={(e) => setUserRole(e.target.value)}
              >
                {[ROLE.NO_LICENSE, ROLE.NORMAL, ROLE.ADMIN, ROLE.SUPER_ADMIN].map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[14px] font-medium text-[#111827] mb-1.5">
                {t('users.columnUserGroup')}
              </label>
              <MultiSelectDropdown
                options={departments.map((d) => ({ code: String(d.id), label: d.department_name }))}
                selectedCodes={departmentIds}
                onChange={setDepartmentIds}
                placeholder={t('users.columnUserGroup')}
                allOptionLabel={t('users.newUserDefaultsAllGroups')}
                aispaceColor={aispaceColor}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 px-6 py-5">
          <button
            onClick={() => onClose(false)}
            disabled={saving}
            className="px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-60"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
            style={{ backgroundColor: aispaceColor }}
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewUserDefaultsModal;
